import { driver, Driver, DriveStep, PopoverDOM } from 'driver.js';

import { EndpointIntro } from './endpoint-intros';

/** Space around a highlighted element, and its corners (the ring matches). */
const STAGE_PADDING = 8;
const STAGE_RADIUS = 12;

export interface PlaygroundTourOptions {
  path: string;
  intro: EndpointIntro;
  /** The phone layout (Request / Response / Code tabs): its own, shorter
   *  steps; see phoneSteps below. */
  phone?: boolean;
  isMac: boolean;
  /** Puts an example into the page's main input; absent hides the examples. */
  useExample?: (text: string) => void;
  /** Opens a docs page in the app (the tour closes first). */
  openDocs: (url: string) => void;
  /** Called as the tour opens, to remember it was shown (see utils/tour-progress.ts). */
  onStart?: () => void;
  /** The endpoint's description from the live catalog (GET /endpoints), the
   *  same text as its API reference; the first card falls back to the
   *  intro's own summary while the catalog hasn't loaded. */
  description?: string;
  /** Renders the page now (Angular's ApplicationRef.tick), so a form card
   *  the tour opened or closed can be measured straight away. */
  render?: () => void;
  /** The request code panel's collapsed state, so its step can show the
   *  code (see codeStep below). */
  codePanel?: { collapsed: () => boolean; setCollapsed: (collapsed: boolean) => void };
}

/**
 * Walks through one endpoint's page: what it's for, then the API key, the
 * main input, the form's other two sections, the run bar, the request code
 * and the response. In the form each step points at something that fits
 * in its scroll box, which scrolls itself to it; Request and Response
 * configuration each get the form to themselves (see FormCardFrame).
 * The sections are found by order (every endpoint form is question,
 * request, response), the rest by their data-tour attribute in
 * playground.html. A step whose element isn't there is skipped.
 */
export function startPlaygroundTour(o: PlaygroundTourOptions): Driver {
  const { intro } = o;
  const section = (i: number) => document.querySelectorAll<HTMLElement>('.form-panel__scroll .query-section')[i];
  // The key picker in the run bar (playground.html); absent with no key to pick.
  const apiKey = '[data-tour="api-key"]';
  // What a tour closed a moment ago leaves until its next frame.
  document.querySelectorAll('.pg-tour-ring, .pg-tour-frame, .pg-tour-more').forEach(el => el.remove());
  // Phones point at each card's header instead (the form isn't a scroll box there).
  const card = o.phone ? null : new FormCardFrame(o.render ?? (() => {}));
  /** Shows form card `i` alone, framed (see FormCardFrame). */
  const cardStep = (i: number): Partial<DriveStep> => ({
    element: () => (section(i) ? card!.frame : undefined) as Element,
    onHighlightStarted: () => card!.open(section(i), [0, 1, 2].filter(j => j !== i).map(section)),
    onDeselected: () => card!.close(),
    // Taller than the form: say how to see the rest.
    data: { render: (popover: PopoverDOM) => card!.overflows() && addScrollHint(popover) },
  });
  const mainInput = () =>
    // The search bar's own box: its host element is inline.
    document.querySelector('.form-panel__scroll [formControlName="question"], .form-panel__scroll [formControlName="query"] .search-bar') as Element;

  const introStep: DriveStep = {
    popover: {
      title: intro.title,
      description: `<span class="pg-tour__path"><b>POST</b>${esc(o.path)}</span>${withCode(esc(noDashes(o.description ?? intro.summary)))}` +
        // No keyboard on a phone.
        (o.phone ? '' : `<span class="pg-tour__keys">Use <kbd>←</kbd> <kbd>→</kbd> to move, <kbd>Esc</kbd> to close.</span>`),
      popoverClass: 'pg-tour pg-tour--intro',
      nextBtnText: 'Start the tour',
      showButtons: ['next', 'close'],
    },
    data: {
      // The full reference, under the text, for who wants it.
      render: (popover: PopoverDOM) => intro.docsId && addDocsLink(popover, o.path, intro.docsId, url => {
        tour.destroy();
        o.openDocs(url);
      }),
    },
  };
  const replayStep: DriveStep = {
    element: '[data-tour="replay"]',
    popover: {
      title: 'See it again any time',
      description: 'Each endpoint has its own tour. Open it from here.',
      side: 'bottom',
      align: 'end',
    },
  };

  const desktopSteps: DriveStep[] = [
    introStep,
    {
      element: mainInput,
      popover: {
        title: `Your ${intro.input}`,
        description: `Type your ${intro.input} here.` + (o.useExample ? ' Or start with one of these:' : ''),
        side: 'right',
        align: 'start',
      },
      data: { render: (popover: PopoverDOM) => o.useExample && addExamples(popover, intro.examples, o.useExample) },
    },
    {
      ...cardStep(1),
      popover: {
        title: 'Request configuration',
        description: `Optional filters for where to look: ${esc(intro.filters)}. Leave them empty to search everything.`,
        side: 'right',
        align: 'start',
      },
    },
    {
      ...cardStep(2),
      popover: {
        title: 'Response configuration',
        description: 'Choose what comes back, and how much of it.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: apiKey,
      popover: {
        title: 'API key',
        description: 'Every query runs as this key and is billed to it, on every endpoint. Use a test key while you try things out.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: '[data-tour="run"]',
      // The form's cards all fold away, so the run bar stands on its own;
      // they come back as they were when you leave the step.
      onHighlightStarted: () => card!.collapseAll([0, 1, 2].map(section)),
      onDeselected: () => card!.close(),
      popover: {
        title: 'Run it',
        description: `The estimate shows what this query will cost. Press <strong>Run query</strong>, or <kbd>${o.isMac ? '⌘' : 'Ctrl'}</kbd> <kbd>Enter</kbd>.`,
        side: 'right',
        align: 'end',
      },
    },
    {
      element: '[data-tour="code"]',
      onHighlightStarted: () => code.open(),
      // Not when keepUp() is only highlighting this same step again (on the
      // panel opening re-created): folding it there would undo the opening.
      onDeselected: () => { if (steps[tour.getActiveIndex() ?? -1]?.element !== '[data-tour="code"]') code.close(); },
      popover: {
        title: 'The same request as code',
        description: 'Ready to copy into your app, in Ruby, TypeScript or cURL. It follows every change you make in the form.',
        // Beside the code, over the form, not on top of it.
        side: 'left',
        align: 'start',
      },
    },
    {
      element: '[data-tour="response"]',
      popover: {
        title: 'The response',
        description: `${esc(intro.receive)} <strong>JSON</strong> shows the raw response, and <strong>Headers</strong> the status and headers.`,
        side: 'left',
        align: 'start',
      },
    },
    replayStep,
  ];

  /** A form card's header, which opens and closes it. */
  const sectionHeader = (i: number) => () => section(i)?.querySelector('.query-section__header') as Element;

  // Phones: the same walk in the tab layout. Each step points at something
  // small enough to show with its popover on a phone screen: the form cards
  // by their headers, Response and Code by their tabs.
  const phoneSteps: DriveStep[] = [
    introStep,
    {
      element: mainInput,
      popover: {
        title: `Your ${intro.input}`,
        description: `Type your ${intro.input} here.` + (o.useExample ? ' Or start with one of these:' : ''),
        side: 'bottom',
        align: 'start',
      },
      data: { render: (popover: PopoverDOM) => o.useExample && addExamples(popover, intro.examples, o.useExample) },
    },
    {
      element: sectionHeader(1),
      popover: {
        title: 'Request configuration',
        description: `Optional filters for where to look: ${esc(intro.filters)}. Tap here to open them, or leave them empty to search everything.`,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: sectionHeader(2),
      popover: {
        title: 'Response configuration',
        description: 'Choose what comes back, and how much of it. Tap here to open it.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: apiKey,
      popover: {
        title: 'API key',
        description: 'Every query runs as this key and is billed to it, on every endpoint. Use a test key while you try things out.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: '[data-tour="run"]',
      popover: {
        title: 'Run it',
        description: 'The estimate shows what this query will cost. Tap <strong>Run query</strong> and the result opens in <strong>Response</strong>.',
        side: 'top',
        align: 'end',
      },
    },
    {
      element: '[data-tour="phone-response"]',
      popover: {
        title: 'The response',
        description: `${esc(intro.receive)} Inside it, <strong>JSON</strong> shows the raw response, and <strong>Headers</strong> the status and headers.`,
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="phone-code"]',
      popover: {
        title: 'The same request as code',
        description: 'Ready to copy into your app, in Ruby, TypeScript or cURL. It follows every change you make in the form.',
        side: 'bottom',
        align: 'end',
      },
    },
    replayStep,
  ];

  const steps = o.phone ? phoneSteps : desktopSteps;

  // The code step shows the code: a collapsed panel opens for it, and
  // folds back when the step is left (only if it opened it).
  let codeOpenedByTour = false;
  /** keepUp() is highlighting the same step again, not arriving at it. */
  let rehighlighting = false;
  const code = {
    open: () => {
      // Arriving only: on a re-highlight the panel is as the person left it
      // (they may have just folded it themselves).
      if (rehighlighting || !o.codePanel?.collapsed()) return;
      o.codePanel.setCollapsed(false);
      codeOpenedByTour = true;
      o.render?.();
    },
    close: () => {
      if (!codeOpenedByTour) return;
      codeOpenedByTour = false;
      if (o.codePanel && !o.codePanel.collapsed()) {
        o.codePanel.setCollapsed(true);
        o.render?.();
      }
    },
  };

  const ring = createRing();

  const tour = driver({
    steps,
    showProgress: true,
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Finish',
    popoverClass: 'pg-tour',
    popoverOffset: 14,
    overlayColor: '#000F33',
    overlayOpacity: 0.55,
    stagePadding: STAGE_PADDING,
    stageRadius: STAGE_RADIUS,
    skipMissingElement: true,
    // Its own arrow keys would turn the page while you type in the
    // highlighted input; see onKey below.
    allowKeyboardControl: false,
    // Brings the element into view inside the form's own scroll box first,
    // so driver.js doesn't scroll the whole page to reach it.
    onHighlightStarted: el => el && revealInForm(el),
    // One hook for every step (a step's own onPopoverRender would replace
    // it): the progress dots, then the step's extras from data.render.
    onPopoverRender: (popover, { state, config }) => {
      const all = config.steps ?? [];
      const index = state.activeIndex ?? 0;
      renderProgress(popover, index, all);
      all[index]?.data?.['render']?.(popover);
    },
  });

  const onKey = (e: KeyboardEvent) => {
    if (!tour.isActive()) { window.removeEventListener('keyup', onKey); return; }
    if (e.key === 'Escape') { tour.destroy(); return; }
    if (isTyping(e.target)) return;
    if (e.key === 'ArrowRight') tour.moveNext();
    if (e.key === 'ArrowLeft' && tour.hasPreviousStep()) tour.movePrevious();
  };
  window.addEventListener('keyup', onKey);

  // ↓ ↑ and Page Down / Up scroll the card a configuration step shows
  // (keydown, so holding a key keeps going; the page itself stays put).
  const onScrollKey = (e: KeyboardEvent) => {
    if (!tour.isActive()) { window.removeEventListener('keydown', onScrollKey); return; }
    if (!card?.showing || isTyping(e.target)) return;
    const step = { ArrowDown: 1, ArrowUp: -1, PageDown: 'page', PageUp: '-page' }[e.key];
    if (!step) return;
    e.preventDefault();
    card.scroll(step === 'page' ? 1 : step === '-page' ? -1 : 0, typeof step === 'number' ? step : 0);
  };
  window.addEventListener('keydown', onScrollKey);

  // The ring follows driver.js's cut-out frame by frame (it animates
  // between steps), and leaves with the tour however it closed.
  const follow = () => {
    if (!tour.isActive()) { ring.remove(); card?.close(); card?.dispose(); code.close(); return; }
    if (card?.sync()) tour.refresh();
    rehighlighting = true;
    keepUp(tour);
    rehighlighting = false;
    placeRing(ring, tour);
    requestAnimationFrame(follow);
  };

  // Shown once is seen, finished or not.
  o.onStart?.();
  tour.drive();
  requestAnimationFrame(follow);
  return tour;
}

/**
 * The Request and Response configuration steps. open() shows one form card
 * on its own: it expands that card and collapses the others, then scrolls
 * the form to it; collapseAll() (Run it) folds them all. close() puts back
 * every card either changed, and only those.
 * `frame` is a fixed, see-through box kept over the part of the card inside
 * the form's scroll box and the window: that's what driver.js highlights,
 * so a tall card neither spills past the form nor makes it scroll the page.
 * sync() moves it, and says if it moved.
 */
class FormCardFrame {
  readonly frame = document.createElement('div');
  /** "More fields ↓" at the frame's foot while the card goes on below. */
  private readonly more = document.createElement('button');
  private card: HTMLElement | null = null;
  private box: HTMLElement | null = null;
  /** Cards this step opened (true) or collapsed (false). */
  private changed = new Map<HTMLElement, boolean>();
  private last = '';

  /** `render` brings the page up to date after a card is toggled, so it can be measured. */
  constructor(private readonly render: () => void) {
    this.frame.className = 'pg-tour-frame';
    this.more.type = 'button';
    this.more.className = 'pg-tour-more';
    this.more.innerHTML = 'More fields <span aria-hidden="true">↓</span>';
    this.more.hidden = true;
    this.more.addEventListener('click', () => this.scroll(0.8, 0));
    document.body.append(this.frame, this.more);
  }

  /** A card is shown (a configuration step is on). */
  get showing(): boolean {
    return !!this.card;
  }

  /** The card runs past the bottom of the form's scroll box. */
  overflows(): boolean {
    if (!this.card || !this.box) return false;
    return this.card.getBoundingClientRect().bottom > this.box.getBoundingClientRect().bottom + 4;
  }

  /** Scrolls the form by `pages` of its height plus `lines` of 80px. */
  scroll(pages: number, lines: number): void {
    if (!this.box) return;
    this.box.scrollBy({ top: pages * this.box.clientHeight + lines * 80, behavior: 'smooth' });
  }

  dispose(): void {
    this.frame.remove();
    this.more.remove();
  }

  open(card: HTMLElement | undefined, others: (HTMLElement | undefined)[]): void {
    // Both steps highlight this same frame, so driver.js doesn't leave one
    // (onDeselected) on its way to the other: put the last one back here,
    // and render, so the cards' classes say how they are now.
    if (this.restore()) this.render();
    for (const other of others) if (other) this.setOpen(other, false);
    if (!card) { this.render(); return; }
    this.card = card;
    card.classList.add('pg-tour-live');
    // The wheel and trackpad scroll the form again (driver.js turns pointer
    // events off around what it highlights).
    this.box = card.closest<HTMLElement>('.form-panel__scroll');
    this.box?.classList.add('pg-tour-scroll');
    this.setOpen(card, true);
    this.render();
    if (this.box) this.box.scrollTop += card.getBoundingClientRect().top - this.box.getBoundingClientRect().top - 16;
    this.sync();
  }

  /** Collapses every card (the Run it step); close() opens them back. */
  collapseAll(cards: (HTMLElement | undefined)[]): void {
    this.open(undefined, cards);
  }

  close(): void {
    if (this.restore()) this.render();
  }

  /** Puts back what the last open() changed; false if there was nothing. */
  private restore(): boolean {
    const card = this.card;
    if (!card && !this.changed.size) return false;
    this.card = null;
    card?.classList.remove('pg-tour-live');
    this.box?.classList.remove('pg-tour-scroll');
    this.box = null;
    this.more.hidden = true;
    for (const [c, opened] of this.changed) {
      if (isOpen(c) === opened) toggle(c);
    }
    this.changed.clear();
    return true;
  }

  private setOpen(card: HTMLElement, open: boolean): void {
    if (isOpen(card) === open) return;
    toggle(card);
    this.changed.set(card, open);
  }

  sync(): boolean {
    if (!this.card) return false;
    const r = this.card.getBoundingClientRect();
    const box = this.box?.getBoundingClientRect();
    const top = Math.max(r.top, box?.top ?? r.top, 0);
    const bottom = Math.min(r.bottom, box?.bottom ?? r.bottom, window.innerHeight);
    this.more.hidden = !this.overflows();
    if (!this.more.hidden) {
      this.more.style.left = `${r.left + r.width / 2}px`;
      this.more.style.top = `${bottom - 16}px`;
    }
    const next = `${r.left},${top},${r.width},${Math.max(0, bottom - top)}`;
    if (next === this.last) return false;
    this.last = next;
    Object.assign(this.frame.style, {
      left: `${r.left}px`, top: `${top}px`, width: `${r.width}px`, height: `${Math.max(0, bottom - top)}px`,
    });
    return true;
  }
}

function isOpen(card: HTMLElement): boolean {
  return !card.classList.contains('query-section--collapsed');
}

/** The card's own header click (the page's toggleSection). */
function toggle(card: HTMLElement): void {
  card.querySelector<HTMLElement>('.query-section__header')?.click();
}

/**
 * Keeps the highlight on what it's showing while the page changes under it.
 * Opening or folding the code panel re-creates it (and, stacked, the
 * response panel), leaving driver.js on an element that's gone: that step
 * is highlighted again, on the new one. One that only changed size (a panel
 * opened in place) is re-measured. Not while driver.js is still moving
 * between steps.
 */
function keepUp(tour: Driver): void {
  if (tour.getState('__transitionCallback')) return;
  const el = tour.getActiveElement();
  const index = tour.getActiveIndex();
  if (!el || index === undefined || el.id === 'driver-dummy-element') return;
  if (!el.isConnected) { tour.moveTo(index); return; }
  const stage = tour.getState('__activeStagePosition') as { x: number; y: number; width: number; height: number } | undefined;
  const r = el.getBoundingClientRect();
  if (stage && (Math.abs(stage.x - r.x) > 1 || Math.abs(stage.y - r.y) > 1
    || Math.abs(stage.width - r.width) > 1 || Math.abs(stage.height - r.height) > 1)) {
    tour.refresh();
  }
}

/** A glowing outline over the cut-out; driver.js only dims around it. */
function createRing(): HTMLElement {
  const ring = document.createElement('div');
  ring.className = 'pg-tour-ring';
  ring.style.borderRadius = `${STAGE_RADIUS}px`;
  document.body.appendChild(ring);
  return ring;
}

function placeRing(ring: HTMLElement, tour: Driver): void {
  const stage = tour.getState('__activeStagePosition') as { x: number; y: number; width: number; height: number } | undefined;
  const centred = tour.getActiveElement()?.id === 'driver-dummy-element';
  if (!stage || centred) { ring.style.opacity = '0'; return; }
  ring.style.opacity = '1';
  ring.style.transform = `translate(${stage.x - STAGE_PADDING}px, ${stage.y - STAGE_PADDING}px)`;
  ring.style.width = `${stage.width + STAGE_PADDING * 2}px`;
  ring.style.height = `${stage.height + STAGE_PADDING * 2}px`;
}

/** Scrolls the form's own box (not the page) so `el` sits near its top. */
function revealInForm(el: Element): void {
  const box = el.closest('.form-panel__scroll');
  if (!box) return;
  const r = el.getBoundingClientRect();
  const b = box.getBoundingClientRect();
  const margin = 16;
  if (r.top >= b.top + margin && r.bottom <= b.bottom - margin) return;
  box.scrollTop += r.top - b.top - margin;
}

/** Dots instead of "3 of 8": the current one is a short bar. */
function renderProgress(popover: PopoverDOM, index: number, steps: DriveStep[]): void {
  const shown = steps.filter(s => !s.element || !!resolve(s.element));
  const at = Math.max(0, shown.indexOf(steps[index]));
  popover.progress.replaceChildren(...shown.map((_, i) => {
    const dot = document.createElement('i');
    if (i === at) dot.className = 'is-active';
    else if (i < at) dot.className = 'is-done';
    return dot;
  }));
  popover.progress.setAttribute('aria-label', `Step ${at + 1} of ${shown.length}`);
}

function resolve(element: DriveStep['element']): Element | null {
  if (typeof element === 'string') return document.querySelector(element);
  if (typeof element === 'function') return element() ?? null;
  return element ?? null;
}

function addExamples(popover: PopoverDOM, examples: string[], use: (text: string) => void): void {
  const list = document.createElement('div');
  list.className = 'pg-tour__examples';
  for (const text of examples) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pg-tour__example';
    btn.textContent = text;
    btn.addEventListener('click', () => {
      use(text);
      list.querySelectorAll('.pg-tour__example').forEach(b => b.classList.toggle('is-picked', b === btn));
    });
    list.appendChild(btn);
  }
  popover.description.appendChild(list);
}

function addScrollHint(popover: PopoverDOM): void {
  const hint = document.createElement('span');
  hint.className = 'pg-tour__keys';
  hint.innerHTML = 'Scroll to see every field, or use <kbd>↓</kbd> <kbd>↑</kbd>.';
  popover.description.appendChild(hint);
}

function addDocsLink(popover: PopoverDOM, path: string, docsId: string, open: (url: string) => void): void {
  const url = `/docs/api-reference/${docsId}`;
  const link = document.createElement('a');
  link.href = url;
  link.className = 'pg-tour__docs';
  link.textContent = `Read the ${path} reference →`;
  link.addEventListener('click', e => {
    e.preventDefault();
    open(url);
  });
  popover.description.insertBefore(link, popover.description.querySelector('.pg-tour__keys'));
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));
}

/** The catalog's " — " asides read as commas on the card. */
function noDashes(text: string): string {
  return text.replace(/\s*—\s*/g, ', ');
}

/** `name` in the catalog's text as code, as the API reference shows it. */
function withCode(html: string): string {
  return html.replace(/`([^`]+)`/g, '<code>$1</code>');
}

function esc(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
